import styled, { css } from "styled-components";
import { typographyMixin } from "../../typography";
import { ButtonProps } from "./Button.types";
import { hex2rgbaForShadows, textColorMixin } from "../../colors";

type StyledButtonProps = Pick<ButtonProps, "theme" | "variant" | "backgroundColor" | "textColor" | "borderColor" | "shadowColor" | "inverted" | "useSecondary" | "activeButton">;

const arrowMixin = ({ degrees }) => `
    width: 12px;
    height: 8px;
    -webkit-transform: rotate(${degrees});
    transform: rotate(${degrees});
  `;

const invertedBackgroundColorMixin = css<StyledButtonProps>`
  ${(props) => props.inverted === "primaryInverted" && props.theme.textOnPrimaryColor}
  ${(props) => props.inverted === "secondaryInverted" && props.theme.textOnSecondaryColor}
`;
const invertedfontColorMixin = css<StyledButtonProps>`
  ${(props) => props.inverted === "primaryInverted" && props.theme.primaryColor};
  ${(props) => props.inverted === "secondaryInverted" && props.theme.secondaryColor};
`;

const secondaryBackgroundColorMixin = css<StyledButtonProps>`
  ${(props) => (props.useSecondary ? props.theme.secondaryColor : props.theme.primaryColor)};
`;

const secondaryFontColorMixin = css<StyledButtonProps>`
  ${(props) => (props.useSecondary ? props.theme.textOnSecondaryColor : props.theme.textOnPrimaryColor)};
`;

const backgroundColorMixin = css<StyledButtonProps>`
  background-color: ${(props) => (props.inverted ? invertedBackgroundColorMixin : secondaryBackgroundColorMixin)};
  background-color: ${(props) => props.variant && props.theme[props.variant]};
  background-color: ${(props) => props.backgroundColor && props.backgroundColor};
`;

const outLineBackgroundColorMixin = css<StyledButtonProps>`
  background-color: transparent;
  background-color: ${(props) => props.backgroundColor && props.backgroundColor};
`;

const fontColorMixin = css<StyledButtonProps>`
  color: ${(props) => {
    if (props.inverted) {
      return invertedfontColorMixin;
    } else if (props.variant === "muted") {
      return "#36383D"; 
    } else if (props.variant) {
      return "#fff"
    }
    else {
      return secondaryFontColorMixin;
    }
  }};
  color: ${(props) => props.textColor && props.textColor};
`;

const outlineFontColorMixin = css<StyledButtonProps>`
color: ${(props) => (props.inverted ? invertedBackgroundColorMixin : secondaryBackgroundColorMixin)};
  color: ${(props) => props.variant && props.theme[props.variant]};
  color: ${(props) => props.textColor && props.textColor};
`;

const invertedBoxShadowMixin = css<StyledButtonProps>`
  ${(props) => props.inverted === "primaryInverted" && hex2rgbaForShadows(props.theme.textOnPrimaryColor, 0.5)};
  ${(props) => props.inverted === "secondaryInverted" && hex2rgbaForShadows(props.theme.textOnSecondaryColor, 0.5)};
`;

const secondaryBoxShadowMixin = css<StyledButtonProps>`
  ${(props) => (props.useSecondary ? hex2rgbaForShadows(props.theme.secondaryColor, 0.5) : hex2rgbaForShadows(props.theme.primaryColor, 0.5))};
`;

const boxShadowMixin = css<StyledButtonProps>`
  box-shadow: ${(props) => (props.inverted ? invertedBoxShadowMixin : secondaryBoxShadowMixin)};
  box-shadow: ${(props) => props.variant && hex2rgbaForShadows(props.theme[props.variant], 0.5)};
  box-shadow: ${(props) => props.shadowColor && hex2rgbaForShadows(props.shadowColor, 0.5)};
`;

const borderColorMixin = css<StyledButtonProps>`
  border-color: ${(props) => (props.inverted ? invertedBackgroundColorMixin : secondaryBackgroundColorMixin)};
  border-color: ${(props) => props.variant && props.theme[props.variant]};
  border-color: ${(props) => props.borderColor && props.borderColor};
`;

export const StyledButton = styled.button<StyledButtonProps>`
  ${typographyMixin}

  text-transform: uppercase;
  display: inline-block;
  text-decoration: none;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  cursor: pointer;
  border-radius: 0.25rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  letter-spacing: 0.1em;
  line-height: 1.5;


  a{
    text-decoration: none;
    color: inherit;
    display: contents;
  }



  &.xxs {
    font-size: 0.55rem;
    line-height: 1;
  }

  &.xs {
    font-size: 0.6rem;
  }

  &.sm {
    font-size: 0.65rem;
  }

  &.md {
    font-size: 0.75rem;
    letter-spacing: .1em;
    padding-top: 0.75em;
    padding-bottom: 0.75em;
    text-decoration: none;
    white-space: nowrap;
    text-align: left;
    align-items: center;
  }

  &.lg {
    font-size: 1rem;
  }

  &.xl {
    font-size: 1.5rem;
  }

  &.xxl {
    font-size: 2rem;
  }

  &.block {
    display: flex;
    justify-content: space-around;
    width: 100%;
  }

  &.solid {
    border-width: 0;

    ${backgroundColorMixin}
    ${fontColorMixin}

    &:hover:not(.disabled ) {
      filter:  ${(props)=> props.activeButton && "brightness(0.95)"};
      ${(props)=> !props.activeButton && boxShadowMixin};
    }

    &:active:not(.disabled) {
      /* padding: 0.425rem 0.7rem 0.325rem 0.8rem; */
    }

    &.active {
      filter: brightness(0.95);
    }
  }

  &.outline {
    padding: calc(0.375rem - 0.1rem) calc(0.75rem - 0.1rem);
    border-width: 0.1rem;
    border-style: solid;

    ${borderColorMixin}
    ${outlineFontColorMixin}
    ${outLineBackgroundColorMixin}
       
    &:hover:not(.disabled) {
      ${boxShadowMixin}
    }

    &:active:not(.disabled) {
      padding: calc(0.425rem - 0.1rem) calc(0.7rem - 0.1rem) calc(0.325rem - 0.1rem) calc(0.8rem - 0.1rem);
    }
  }

  .arrow-left {
    margin-right: 0.75rem;
  }

  .arrow-right,
  .arrow-dropdown, .arrow-up {
    margin-left: 0.75rem;
  }

  &.arrow-left {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: center;
    &::before {
      ${arrowMixin({
        degrees: "90deg",
      })}

      margin-right: 0.75rem;
      margin-left: -0.25rem;
    }
    &.block {
      text-align: right;
      &::before {
        margin-top: 0.375rem;
      }
    }
  }

  &.arrow-right {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: center;
    &::after {
      ${arrowMixin({
        degrees: "-90deg",
      })}
      margin-left: 0.75rem;
      margin-right: -0.25rem;
    }
    &.block {
      text-align: left;
      &::after {
        margin-top: 0.375rem;
      }
    }
  }

  &.arrow-dropdown {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &::after {
      ${arrowMixin({
        degrees: "0deg",
      })}
      margin-left: 0.75rem;
      margin-right: -0.25rem;
    }
    &.block {
      text-align: right;
      &::after {
        margin-top: 0.375rem;
      }
    }
  }

  &.arrow-up {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &::after {
      ${arrowMixin({
        degrees: "0deg",
      })}
      margin-left: .75rem;
      margin-right: -0.25rem;
    }
    &.block {
      text-align: right;
      &::after {
        margin-top: 0.375rem;
      }
    }
  }

  &.disabled {
    cursor: default;
    opacity: .5;
  }

`;

export const StyledButtonGroup = styled.div`
  display: inline-block;

  &.divider-divided {
    .button:not(.outline) {
      border-left-color: #fffffe55;
      margin-left: -0.1rem;
      border-left-width: 0.1rem !important;
      margin-right: -0.1rem;
      border-right-width: 0.1rem !important;
    }
  }

  .button {
    border-radius: 0rem;

    &:first-child {
      border-radius: 0.25rem 0 0 0.25rem;
    }

    &:last-child {
      border-radius: 0 0.25rem 0.25rem 0;
    }
  }
`;
